import {fmt} from './format';
import {STAMM_NONE, stammDiffLabel} from './availability';

// Änderungs-Erkennung und Diff-Aufbau des Auftrags-Editors (Original
// `isDirty` 5326, `kitChildStammChanged` 5333, die `*Changed`-Prüfungen
// 5341–5346, `computeChanges` 5134, `updateSum` 5351 und der Diff-Teil von
// `doSave` 5216).
//
// Grundsatz des Originals: Gespeichert wird nur, was sich gegenüber dem
// geladenen Stand geändert hat („Diff-Save"). Die Baustelle eines zweiten
// Bearbeiters in weclapp bleibt dadurch erhalten. Jede Position trägt ihren
// Ausgangsstand als JSON in `_orig` (rows.js), die Kopf-Änderungen liegen als
// „vorgemerkt" im Zustand und werden erst beim Speichern wirksam.

/** Vorgemerkte Kopf-Änderungen im Ausgangszustand (Original PENDING_*). */
export const emptyPending = {
    channel: null,
    warehouse: null,
    payType: null,
    advance: null,
    custOrderNo: null,
    shipCost: [],
    // Der Versandschritt gilt als „angefasst", sobald eine Zeile hinzukommt
    // oder die vorhandenen Versandkosten-Positionen bewusst zurückgesetzt
    // wurden — erst dann geht beim Speichern überhaupt ein `shippingCost`
    // mit, auch als leeres Array (sonst ließe sich nie etwas entfernen).
    shipCostTouched: false,
    // Nur über „Alle vorhandenen Versandkosten entfernen": Dann ersetzt der
    // Server die freigegebenen Versandkosten-Zeilen des Auftrags, statt die
    // vorgemerkten nur anzuhängen. Ohne diesen Schalter bleibt alles, was
    // schon am Auftrag steht, erhalten.
    shipCostReset: false,
    custDesc: null,
};

/** Ausgangsstand einer Position (Original `JSON.parse(r._orig)`). */
export function origOf(r) {
    try {
        return JSON.parse(r._orig);
    } catch (e) {
        return null;
    }
}

/**
 * Weicht die Position vom geladenen Stand ab (Original `isDirty`, 5326)?
 * Neue Zeilen sind immer geändert.
 */
export function isDirty(r) {
    if (r._new) {
        return true;
    }
    const o = origOf(r);
    if (o === null) {
        return true;
    }
    return String(o.quantity) !== String(r.quantity)
        || String(o.discountPercentage || '0') !== String(r.discountPercentage || '0')
        || String(o.stammOverride || '') !== String(r._stammOverride || '');
}

/**
 * Kit-Kinder (Set-Inhalt) sind gesperrt — Menge und Rabatt folgen dem Pack.
 * Die EINZIGE speicherbare Änderung ist ihre Lagerwahl, damit ein Set-Split
 * über zwei Läger den Neuladen übersteht (Original `kitChildStammChanged`,
 * 5333; ABGLEICH H26/J43/J59).
 */
export function kitChildStammChanged(r, mode) {
    if (mode !== 'order' || !r.locked || r._new || r.del || !r.articleId) {
        return false;
    }
    const o = origOf(r);
    if (o === null) {
        return false;
    }
    return String(o.stammOverride || '') !== String(r._stammOverride || '');
}

export function channelChanged(pending, order) {
    return !!(pending.channel && order && pending.channel !== order.salesChannel);
}

export function warehouseChanged(pending, order) {
    return !!(pending.warehouse && order && String(pending.warehouse) !== String(order.warehouseId));
}

export function payTypeChanged(pending, order) {
    return !!(pending.payType && order && order.payment && pending.payType !== order.payment.current);
}

export function custOrderNoChanged(pending, order) {
    return !!(pending.custOrderNo != null && order && pending.custOrderNo !== (order.customerOrderNumber || ''));
}

/**
 * Anzahlungssumme geändert — zählt nur, wenn die **effektive** Auftragsart
 * Anzahlung ist (Original `advanceChanged`, 5346).
 */
export function advanceChanged(pending, order) {
    if (pending.advance == null || !order || !order.payment) {
        return false;
    }
    if ((pending.payType || order.payment.current) !== 'ADVANCE_PAYMENT') {
        return false;
    }
    return pending.advance !== order.payment.advanceCurrent;
}

/** Kundeninfo (Kundenstamm) vorgemerkt? */
export function custDescChanged(pending) {
    return pending.custDesc != null;
}

/**
 * Offene Änderungen insgesamt (Original `hasUnsavedEdits`, 1996). Grundlage
 * für die Frische-Prüfung und die Warnung beim Verlassen der Seite.
 *
 * Abweichung vom Original: Die vorgemerkte Kundeninfo zählt hier mit. Im
 * Original fehlt sie sowohl hier als auch in `updateSum` — eine allein
 * geänderte Kundeninfo ließe sich dort gar nicht speichern.
 */
export function hasUnsavedEdits({rows, mode, pending, order}) {
    const zeilen = (rows || []).some(
        (r) => (!r.locked && (r.del || isDirty(r))) || kitChildStammChanged(r, mode),
    );
    return zeilen
        || channelChanged(pending, order)
        || warehouseChanged(pending, order)
        || payTypeChanged(pending, order)
        || advanceChanged(pending, order)
        || custOrderNoChanged(pending, order)
        || pending.shipCostTouched
        || custDescChanged(pending);
}

/**
 * Zahl der Änderungen für die Beschriftung „N Änderungen speichern"
 * (Original `updateSum`, 5351).
 */
export function countChanges({rows, mode, pending, order}) {
    const liste = rows || [];
    return liste.filter((r) => !r.locked && !r.del && isDirty(r)).length
        + liste.filter((r) => kitChildStammChanged(r, mode)).length
        + liste.filter((r) => r.del).length
        + (channelChanged(pending, order) ? 1 : 0)
        + (warehouseChanged(pending, order) ? 1 : 0)
        + (payTypeChanged(pending, order) ? 1 : 0)
        + (advanceChanged(pending, order) ? 1 : 0)
        + (custOrderNoChanged(pending, order) ? 1 : 0)
        + (pending.shipCostTouched ? Math.max(1, (pending.shipCost || []).length) : 0)
        + (custDescChanged(pending) ? 1 : 0);
}

/** Anzeigename eines Vertriebswegs aus dem Beleg. */
function channelLabel(code, order) {
    return (((order && order.channels) || []).find((x) => x.code === code) || {}).label || code;
}

/** Anzeigename eines Lagers aus dem Beleg. */
function warehouseLabel(id, order) {
    return (((order && order.warehouses) || []).find((x) => String(x.id) === String(id)) || {}).name || id;
}

/** Anzeigename einer Auftragsart. */
function payTypeLabel(code, order) {
    const labels = (order && order.payment && order.payment.labels) || {};
    return labels[code] || code || '—';
}

/**
 * Klartext-Liste für das Prüfmodal (Original `computeChanges` 5134 und die
 * beiden `unshift`-Zeilen in `save()` 5160). Reihenfolge wie im Original:
 * erst Auftragslager, dann Vertriebsweg, dann die Positionen.
 *
 * `t` steuert die Farbe: `chg` blau (geändert), `neu` grün (neu), `weg` rot
 * (entfällt).
 *
 * @returns {{t: string, txt: string}[]}
 */
export function computeChanges({rows, mode, pending, order}) {
    const out = [];
    const hasPrice = mode === 'order';

    (rows || []).forEach((r) => {
        if (r.locked) {
            if (kitChildStammChanged(r, mode)) {
                const o = origOf(r) || {};
                out.push({
                    t: 'chg',
                    txt: `${r.articleNumber} ${r.title || ''} (Set-Inhalt): Stammlager `
                        + `${stammDiffLabel(o.stammOverride)} → ${stammDiffLabel(r._stammOverride)}`,
                });
            }
            return;
        }
        if (r.del) {
            out.push({t: 'weg', txt: `Pos ${r.articleNumber} ${r.title || ''} — wird gelöscht`});
            return;
        }
        if (r._new) {
            out.push({
                t: 'neu',
                txt: `Neu: ${r.articleNumber} ${r.title || ''} — Menge ${r.quantity}`
                    + (r._stammOverride ? ` · Stammlager ${stammDiffLabel(r._stammOverride)}` : ''),
            });
            return;
        }
        const o = origOf(r);
        if (o === null) {
            return;
        }
        if (String(o.quantity) !== String(r.quantity)) {
            out.push({t: 'chg', txt: `${r.articleNumber} ${r.title || ''}: Menge ${o.quantity} → ${r.quantity}`});
        }
        if (hasPrice && String(o.discountPercentage || '0') !== String(r.discountPercentage || '0')) {
            out.push({
                t: 'chg',
                txt: `${r.articleNumber} ${r.title || ''}: Rabatt ${o.discountPercentage || '0'} % → ${r.discountPercentage} %`,
            });
        }
        if (String(o.stammOverride || '') !== String(r._stammOverride || '')) {
            out.push({
                t: 'chg',
                txt: `${r.articleNumber} ${r.title || ''}: Stammlager `
                    + `${stammDiffLabel(o.stammOverride)} → ${stammDiffLabel(r._stammOverride)}`,
            });
        }
    });

    // Kopf-Änderungen. Auftragsart, Anzahlung, Kunden-Bestellnummer und
    // Versandkosten stehen im Original zwar im Änderungszähler, fehlen dort
    // aber in der Prüfliste — sie werden hier ergänzt, weil im Prüfmodal
    // sonst eine gezählte Änderung ohne Zeile stünde.
    if (custDescChanged(pending)) {
        out.push({t: 'chg', txt: '✎ Kundeninfo (Kundenstamm) geändert — wirkt auf alle Belege dieses Kunden'});
    }
    (pending.shipCost || []).forEach((s) => {
        out.push({
            t: 'neu',
            txt: `Versandkosten: ${s.qty}× ${s.articleNumber || s.articleId} à ${fmt(s.pricePer)} €`,
        });
    });
    if (pending.shipCostReset && (order.shippingCosts || []).length) {
        out.push({t: 'weg', txt: 'Versandkosten: vorhandene Positionen werden entfernt'});
    }
    if (custOrderNoChanged(pending, order)) {
        out.push({
            t: 'chg',
            txt: `Kunden-Bestellnr.: ${order.customerOrderNumber || '—'} → ${pending.custOrderNo || '—'}`,
        });
    }
    if (advanceChanged(pending, order)) {
        out.push({
            t: 'chg',
            txt: `Anzahlung: ${fmt(order.payment.advanceCurrent)} € → ${fmt(pending.advance)} €`,
        });
    }
    if (payTypeChanged(pending, order)) {
        out.push({
            t: 'chg',
            txt: `Auftragsart: ${payTypeLabel(order.payment.current, order)} → ${payTypeLabel(pending.payType, order)}`,
        });
    }
    if (channelChanged(pending, order)) {
        out.unshift({
            t: 'neu',
            txt: `Vertriebsweg: ${channelLabel(order.salesChannel, order)} → ${channelLabel(pending.channel, order)}`
                + ' — alle nicht-versendeten Positionen werden von weclapp neu bepreist',
        });
    }
    if (warehouseChanged(pending, order)) {
        out.unshift({
            t: 'neu',
            txt: `Auftragslager: ${warehouseLabel(order.warehouseId, order)} → ${warehouseLabel(pending.warehouse, order)}`,
        });
    }

    return out;
}

/**
 * Der Rumpf für `POST /order-editor/{ref}/save` (Original `doSave`, 5216).
 * Es geht ausschließlich das Diff hinaus.
 */
export function buildSaveChanges({rows, mode, pending, order}) {
    const changes = {upd: [], del: [], add: []};

    (rows || []).forEach((r) => {
        if (r.locked) {
            // Gesperrtes Kit-Kind: nur die Lagerwahl ist speicherbar. Menge und
            // Rabatt müssen unverändert mitgehen, sonst setzt der Server die
            // Menge auf „undefined".
            if (kitChildStammChanged(r, mode)) {
                changes.upd.push({
                    id: r.id,
                    quantity: r.quantity,
                    discountPercentage: r.discountPercentage,
                    stammOverride: r._stammOverride || '',
                });
            }
            return;
        }
        if (r.del && !r._new) {
            changes.del.push(r.id);
            return;
        }
        if (r._new && !r.del) {
            changes.add.push({
                articleId: r.articleId,
                quantity: r.quantity,
                discountPercentage: r.discountPercentage,
                ...(r._stammOverride ? {stammOverride: r._stammOverride} : {}),
                ...(r._mbRest && r._after ? {after: r._after} : {}),
                ...(r._shipCost && r._unitPrice != null ? {unitPrice: r._unitPrice} : {}),
            });
            return;
        }
        if (!r._new && !r.del && isDirty(r)) {
            const u = {id: r.id, quantity: r.quantity, discountPercentage: r.discountPercentage};
            // Die Lagerwahl nur mitschicken, wenn sie sich geändert hat
            // (Leerstring entfernt sie, `none` heißt „keine Lieferung").
            const o = origOf(r);
            if (o !== null && String(o.stammOverride || '') !== String(r._stammOverride || '')) {
                u.stammOverride = r._stammOverride || '';
            }
            changes.upd.push(u);
        }
    });

    if (channelChanged(pending, order)) {
        changes.salesChannel = pending.channel;
    }
    if (warehouseChanged(pending, order)) {
        changes.warehouseId = pending.warehouse;
    }
    if (payTypeChanged(pending, order)) {
        changes.salesOrderPaymentType = pending.payType;
    }
    if (advanceChanged(pending, order)) {
        changes.advancePaymentAmount = pending.advance;
    }
    if (custOrderNoChanged(pending, order)) {
        changes.customerOrderNumber = pending.custOrderNo;
    }
    if (pending.shipCostTouched) {
        // Als (ggf. leeres) Array mitgeben, sobald der Schritt angefasst
        // wurde. Ersetzt wird nur mit `shippingCostReplace`, sonst hängt der
        // Server die Zeilen an die vorhandenen an.
        changes.shippingCost = (pending.shipCost || []).map((s) => ({
            articleId: s.articleId,
            qty: s.qty,
            unitPrice: s.pricePer,
            discountPercentage: s.discountPercentage,
        }));
        if (pending.shipCostReset) {
            changes.shippingCostReplace = true;
        }
    }
    if (custDescChanged(pending)) {
        changes.customerDescription = pending.custDesc;
    }

    return changes;
}

/**
 * Positionen ohne Lieferung als Zuordnung `{itemId: 'none'}` für den
 * Arbeitsstand (`PUT /order-editor/{ref}/state`). Der Wert gehört nicht ins
 * weclapp-Feld „Stammlager", weil „Keine" kein Lager ist (ABGLEICH H34).
 */
export function itemOverridesOf(rows) {
    const out = {};
    (rows || []).forEach((r) => {
        if (r.id != null && r._stammOverride === STAMM_NONE) {
            out[String(r.id)] = STAMM_NONE;
        }
    });
    return out;
}
